import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useAlertFeedCardConfig, useMergedAlertFeedEvents } from './hooks';
import { useAlertsData } from '@/features/EventsListLayout/hooks';
import { useOpenInNewWindow } from '@/hooks';
import { AppRoutes } from '@/constants/routes';
import type { FeedEmptyStateProps } from './components';
import type { AlertRuleEvent } from '@/types/event';

interface SourceAlertsFeedProps {
    sourceId: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function SourceAlertsFeed({
    sourceId,
    titleContent,
    emptyState,
}: SourceAlertsFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const { openEvent } = useOpenInNewWindow();
    const cardConfig = useAlertFeedCardConfig();

    const { events, isLoading, isLoadingMore, hasMore, loadMore } =
        useAlertsData({ sourceId, pageKey: `source-alerts-${sourceId}` });

    const { itemKey } = cardConfig;
    const filterBySource = useCallback(
        (alert: AlertRuleEvent) => alert.event.sourceId === sourceId,
        [sourceId],
    );
    const feedEvents = useMergedAlertFeedEvents(
        events,
        itemKey,
        filterBySource,
    );

    return (
        <GenericEventFeed
            {...cardConfig}
            containerTitle={t('sidebar.sourceAlerts')}
            titleContent={titleContent}
            onViewAll={() =>
                navigate(`${AppRoutes.ALERTS}?sources=${sourceId}`)
            }
            items={feedEvents}
            isLoading={isLoading}
            emptyState={emptyState}
            itemSelectPayload={(alert) => ({
                ...alert.event,
                focuses: alert.focuses,
            })}
            onNavigate={(alert, clickEvent) =>
                openEvent(alert.event.id, clickEvent, {
                    state: {
                        feedContext: { type: 'sourceAlerts', sourceId },
                    },
                })
            }
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
            quickViewContextType="event-feed"
        />
    );
}
